// Lista contratti di un'azienda con creazione e gestione documenti allegati
import { useState, useCallback, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, FileText, Download, Trash2, ChevronDown, ChevronUp, FilePlus, Sparkles, Loader2, AlertCircle } from 'lucide-react'
import { Button } from '../../../components/ui/Button'
import { Input } from '../../../components/ui/Input'
import { Modal } from '../../../components/ui/Modal'
import { FileUpload } from '../../../components/shared/FileUpload'
import api from '../../../lib/api'

interface UploadedFile {
  file: File
  preview?: string
}

interface Contract {
  id: string
  title?: string
  contractNumber?: string
  startDate?: string
  endDate?: string
  monthlyValue?: number
  totalValue?: number
  status?: string
  description?: string
}

interface ContractDocMeta {
  id: string
  filename: string
  mimeType: string
  size: number
  createdAt: string
}

interface ExtractedContractFields {
  contractNumber?: string
  title?: string
  startDate?: string
  endDate?: string
  monthlyValue?: number
  totalValue?: number
  description?: string
  notes?: string
}

interface ExtractedSchedule {
  siteId?: string | null
  siteDescription: string
  title: string
  description?: string
  frequency: string
  firstDueDate?: string | null
  notes?: string | null
}

interface AnalysisResult {
  contractFields: ExtractedContractFields
  maintenanceSchedules: ExtractedSchedule[]
  rawSummary: string
}

const FREQ_LABELS: Record<string, string> = {
  monthly: 'Mensile',
  quarterly: 'Trimestrale',
  biannual: 'Semestrale',
  annual: 'Annuale',
  other: 'Altro',
}

const contractSchema = z.object({
  contractNumber: z.string().min(1, 'Numero contratto obbligatorio'),
  title: z.string().min(1, 'Titolo obbligatorio'),
  startDate: z.string().min(1, 'Data inizio obbligatoria'),
  endDate: z.string().optional(),
  description: z.string().optional(),
  monthlyValue: z.preprocess(
    (v) => (v === '' || v === null || v === undefined ? undefined : Number(v)),
    z.number().positive().optional(),
  ),
  totalValue: z.preprocess(
    (v) => (v === '' || v === null || v === undefined ? undefined : Number(v)),
    z.number().positive().optional(),
  ),
  status: z.string().optional(),
})

type ContractFormData = z.infer<typeof contractSchema>

interface ContractsListProps {
  companyId: string
}

export function ContractsList({ companyId }: ContractsListProps) {
  const queryClient = useQueryClient()
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [uploadContractId, setUploadContractId] = useState<string | null>(null)
  const [pendingFiles, setPendingFiles] = useState<UploadedFile[]>([])
  const [formOpen, setFormOpen] = useState(false)
  const [editingContract, setEditingContract] = useState<Contract | null>(null)
  const [analyzingId, setAnalyzingId] = useState<string | null>(null)
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null)
  const [analysisError, setAnalysisError] = useState<string | null>(null)

  const { data: contracts = [], isLoading } = useQuery<Contract[]>({
    queryKey: ['contracts', 'company', companyId],
    queryFn: async () => {
      const res = await api.get<Contract[]>('/contracts', { params: { companyId } })
      const payload = res.data
      return Array.isArray(payload) ? payload : (payload as { items?: Contract[] })?.items ?? []
    },
    enabled: Boolean(companyId),
  })

  // Documenti del contratto espanso
  const { data: docs = [], refetch: refetchDocs } = useQuery<ContractDocMeta[]>({
    queryKey: ['contract-documents', expandedId],
    queryFn: async () => {
      const res = await api.get<ContractDocMeta[]>(`/contracts/${expandedId}/documents`)
      return res.data
    },
    enabled: Boolean(expandedId),
  })

  // Form contratto
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ContractFormData>({
    resolver: zodResolver(contractSchema),
    defaultValues: { contractNumber: '', title: '', startDate: '', endDate: '', description: '', status: 'active' },
  })

  useEffect(() => {
    if (formOpen) {
      reset({
        contractNumber: editingContract?.contractNumber ?? '',
        title: editingContract?.title ?? '',
        startDate: editingContract?.startDate ?? '',
        endDate: editingContract?.endDate ?? '',
        description: editingContract?.description ?? '',
        monthlyValue: editingContract?.monthlyValue ?? undefined,
        totalValue: editingContract?.totalValue ?? undefined,
        status: editingContract?.status ?? 'active',
      })
    }
  }, [formOpen, editingContract, reset])

  const saveMutation = useMutation({
    mutationFn: async (data: ContractFormData) => {
      const payload = { ...data, companyId }
      if (editingContract?.id) {
        await api.patch(`/contracts/${editingContract.id}`, payload)
      } else {
        await api.post('/contracts', payload)
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['contracts', 'company', companyId] })
      setFormOpen(false)
      setEditingContract(null)
    },
  })

  const uploadMutation = useMutation({
    mutationFn: async ({ contractId, files }: { contractId: string; files: UploadedFile[] }) => {
      const formData = new FormData()
      files.forEach((f) => formData.append('files', f.file))
      await api.post(`/contracts/${contractId}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    },
    onSuccess: () => {
      void refetchDocs()
      void queryClient.invalidateQueries({ queryKey: ['contract-documents', uploadContractId] })
      setPendingFiles([])
      setUploadContractId(null)
    },
  })

  const analyzeMutation = useMutation({
    mutationFn: async (contractId: string) => {
      const res = await api.post<AnalysisResult>(`/contracts/${contractId}/analyze`)
      return res.data
    },
    onSuccess: (data) => {
      setAnalysisResult(data)
      setAnalysisError(null)
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
      setAnalysisError(msg ?? 'Errore durante l\'analisi AI.')
      setAnalyzingId(null)
    },
  })

  const applyMutation = useMutation({
    mutationFn: async ({ contractId, result }: { contractId: string; result: AnalysisResult }) => {
      await api.post(`/contracts/${contractId}/apply-analysis`, {
        contractFields: result.contractFields,
        maintenanceSchedules: result.maintenanceSchedules,
      })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['contracts', 'company', companyId] })
      setAnalysisResult(null)
      setAnalyzingId(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async ({ contractId, docId }: { contractId: string; docId: string }) => {
      await api.delete(`/contracts/${contractId}/documents/${docId}`)
    },
    onSuccess: () => void refetchDocs(),
  })

  const handleUploadConfirm = useCallback(() => {
    if (!uploadContractId || pendingFiles.length === 0) return
    uploadMutation.mutate({ contractId: uploadContractId, files: pendingFiles })
  }, [uploadContractId, pendingFiles, uploadMutation])

  const textareaClass =
    'w-full rounded border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm text-[var(--fc-foreground)] placeholder:text-[var(--fc-foreground-muted)] focus:border-[var(--fc-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--fc-primary)]'

  if (isLoading) {
    return <div className="py-6 text-center text-sm text-[var(--fc-foreground-muted)]">Caricamento contratti...</div>
  }

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-[var(--fc-foreground-muted)]">
          {contracts.length} {contracts.length === 1 ? 'contratto' : 'contratti'}
        </p>
        <Button
          size="sm"
          leftIcon={<Plus className="h-4 w-4" />}
          onClick={() => { setEditingContract(null); setFormOpen(true) }}
        >
          Nuovo contratto
        </Button>
      </div>

      {analysisError && (
        <div className="flex items-start gap-2 rounded-lg border border-[var(--fc-danger)] bg-[color-mix(in_srgb,var(--fc-danger)_10%,transparent)] px-3 py-2 text-sm text-[var(--fc-danger)]">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>{analysisError}</span>
        </div>
      )}

      {contracts.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 text-center">
          <FileText className="h-10 w-10 text-[var(--fc-foreground-muted)] mb-2" />
          <p className="text-sm text-[var(--fc-foreground-muted)]">Nessun contratto registrato</p>
          <Button
            size="sm"
            variant="secondary"
            className="mt-3"
            onClick={() => { setEditingContract(null); setFormOpen(true) }}
          >
            Aggiungi il primo contratto
          </Button>
        </div>
      ) : (
        <ul className="space-y-2">
          {contracts.map((contract) => {
            const isExpanded = expandedId === contract.id

            return (
              <li
                key={contract.id}
                className="rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] overflow-hidden"
              >
                {/* Header contratto */}
                <div className="flex items-start justify-between p-3">
                  <div className="space-y-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-sm text-[var(--fc-foreground)]">
                        {contract.title ?? 'Contratto'}
                      </p>
                      {contract.contractNumber && (
                        <span className="text-xs text-[var(--fc-foreground-muted)]">#{contract.contractNumber}</span>
                      )}
                      {contract.status && (
                        <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                          contract.status === 'active'
                            ? 'bg-[color-mix(in_srgb,var(--fc-success)_15%,transparent)] text-[var(--fc-success)]'
                            : 'bg-[var(--fc-surface-overlay)] text-[var(--fc-foreground-muted)]'
                        }`}>
                          {contract.status === 'active' ? 'Attivo' : contract.status}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-[var(--fc-foreground-muted)]">
                      {contract.startDate ?? '—'} → {contract.endDate ?? 'in corso'}
                    </p>
                    {(contract.monthlyValue || contract.totalValue) && (
                      <p className="text-xs text-[var(--fc-foreground-muted)]">
                        {contract.monthlyValue
                          ? `€ ${Number(contract.monthlyValue).toLocaleString('it-IT')}/mese`
                          : `€ ${Number(contract.totalValue).toLocaleString('it-IT')} totale`}
                      </p>
                    )}
                  </div>

                  <div className="flex gap-1 shrink-0 ml-2">
                    {/* Analisi AI */}
                    <Button
                      size="icon-sm"
                      variant="ghost"
                      onClick={() => {
                        setAnalyzingId(contract.id)
                        setAnalysisError(null)
                        setAnalysisResult(null)
                        analyzeMutation.mutate(contract.id)
                      }}
                      disabled={analyzeMutation.isPending && analyzingId === contract.id}
                      aria-label="Analisi AI contratto"
                      title="Analisi AI contratto"
                    >
                      {analyzeMutation.isPending && analyzingId === contract.id
                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        : <Sparkles className="h-3.5 w-3.5" />}
                    </Button>
                    {/* Allega documento */}
                    <Button
                      size="icon-sm"
                      variant="ghost"
                      onClick={() => { setUploadContractId(contract.id); setPendingFiles([]) }}
                      aria-label="Allega documento"
                      title="Allega documento"
                    >
                      <FilePlus className="h-3.5 w-3.5" />
                    </Button>
                    {/* Espandi documenti */}
                    <Button
                      size="icon-sm"
                      variant="ghost"
                      onClick={() => setExpandedId(isExpanded ? null : contract.id)}
                      aria-label="Mostra documenti"
                      aria-expanded={isExpanded}
                    >
                      {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                    </Button>
                  </div>
                </div>

                {/* Documenti espandibili */}
                {isExpanded && (
                  <div className="border-t border-[var(--fc-border)] px-3 py-3 bg-[var(--fc-surface-overlay)]">
                    {docs.length === 0 ? (
                      <p className="text-xs text-[var(--fc-foreground-muted)]">
                        Nessun documento allegato.{' '}
                        <button
                          type="button"
                          className="underline hover:text-[var(--fc-primary)]"
                          onClick={() => { setUploadContractId(contract.id); setPendingFiles([]) }}
                        >
                          Aggiungi
                        </button>
                      </p>
                    ) : (
                      <ul className="space-y-1">
                        {docs.map((doc) => (
                          <li
                            key={doc.id}
                            className="flex items-center gap-2 rounded border border-[var(--fc-border)] bg-[var(--fc-surface)] px-2 py-1.5"
                          >
                            <FileText className="h-3.5 w-3.5 shrink-0 text-[var(--fc-foreground-muted)]" />
                            <span className="flex-1 truncate text-xs text-[var(--fc-foreground)]">{doc.filename}</span>
                            <span className="shrink-0 text-xs text-[var(--fc-foreground-muted)]">
                              {(doc.size / 1024).toFixed(0)} KB
                            </span>
                            <a
                              href={`${api.defaults.baseURL}/contracts/${contract.id}/documents/${doc.id}/download`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="shrink-0 text-[var(--fc-foreground-muted)] hover:text-[var(--fc-primary)] transition-colors"
                              aria-label={`Scarica ${doc.filename}`}
                            >
                              <Download className="h-3.5 w-3.5" />
                            </a>
                            <button
                              type="button"
                              onClick={() => deleteMutation.mutate({ contractId: contract.id, docId: doc.id })}
                              className="shrink-0 text-[var(--fc-foreground-muted)] hover:text-[var(--fc-danger)] transition-colors"
                              aria-label={`Elimina ${doc.filename}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      )}

      {/* Modal creazione/modifica contratto */}
      <Modal
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditingContract(null) }}
        title={editingContract ? `Modifica: ${editingContract.title ?? 'contratto'}` : 'Nuovo contratto'}
        size="lg"
      >
        <form onSubmit={handleSubmit((data) => saveMutation.mutate(data))} className="space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Input
              label="Numero contratto *"
              {...register('contractNumber')}
              error={errors.contractNumber?.message}
              placeholder="2024-001"
            />
            <Input
              label="Titolo *"
              {...register('title')}
              error={errors.title?.message}
              placeholder="Contratto manutenzione impianti"
            />
            <Input
              label="Data inizio *"
              type="date"
              {...register('startDate')}
              error={errors.startDate?.message}
            />
            <Input
              label="Data fine"
              type="date"
              {...register('endDate')}
            />
            <Input
              label="Valore mensile (€)"
              type="number"
              step="0.01"
              {...register('monthlyValue')}
              error={errors.monthlyValue?.message}
              placeholder="500.00"
            />
            <Input
              label="Valore totale (€)"
              type="number"
              step="0.01"
              {...register('totalValue')}
              error={errors.totalValue?.message}
              placeholder="6000.00"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-[var(--fc-foreground)]">Descrizione</label>
            <textarea
              {...register('description')}
              rows={3}
              className={textareaClass}
              placeholder="Descrizione del contratto, servizi inclusi..."
            />
          </div>
          {saveMutation.isError && (
            <p className="text-sm text-[var(--fc-danger)]">Errore durante il salvataggio. Riprova.</p>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="secondary" onClick={() => { setFormOpen(false); setEditingContract(null) }}>
              Annulla
            </Button>
            <Button type="submit" disabled={isSubmitting || saveMutation.isPending}>
              {saveMutation.isPending ? 'Salvataggio...' : editingContract ? 'Salva modifiche' : 'Crea contratto'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Modal conferma analisi AI */}
      <Modal
        open={Boolean(analysisResult)}
        onClose={() => { setAnalysisResult(null); setAnalyzingId(null) }}
        title="Risultato analisi AI contratto"
        size="lg"
      >
        {analysisResult && (
          <div className="space-y-4">
            {/* Campi contratto estratti */}
            {Object.keys(analysisResult.contractFields).length > 0 && (
              <div>
                <p className="text-sm font-medium text-[var(--fc-foreground)] mb-2">Campi estratti</p>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                  {Object.entries(analysisResult.contractFields).map(([k, v]) =>
                    v != null ? (
                      <div key={k} className="col-span-1">
                        <dt className="text-[var(--fc-foreground-muted)] capitalize">{k}</dt>
                        <dd className="text-[var(--fc-foreground)] font-medium">{String(v)}</dd>
                      </div>
                    ) : null,
                  )}
                </dl>
              </div>
            )}

            {/* Manutenzioni preventive */}
            {analysisResult.maintenanceSchedules.length > 0 && (
              <div>
                <p className="text-sm font-medium text-[var(--fc-foreground)] mb-2">
                  Manutenzioni preventive ({analysisResult.maintenanceSchedules.length})
                </p>
                <ul className="space-y-2">
                  {analysisResult.maintenanceSchedules.map((s, i) => (
                    <li
                      key={i}
                      className="rounded border border-[var(--fc-border)] bg-[var(--fc-surface-overlay)] px-3 py-2 text-xs space-y-0.5"
                    >
                      <p className="font-medium text-[var(--fc-foreground)]">{s.title}</p>
                      <p className="text-[var(--fc-foreground-muted)]">
                        Sede: {s.siteDescription} · Frequenza: {FREQ_LABELS[s.frequency] ?? s.frequency}
                        {s.firstDueDate ? ` · Prima scadenza: ${s.firstDueDate}` : ''}
                      </p>
                      {s.notes && <p className="text-[var(--fc-foreground-muted)] italic">{s.notes}</p>}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Note / sommario */}
            {analysisResult.contractFields.notes && (
              <div>
                <p className="text-sm font-medium text-[var(--fc-foreground)] mb-1">Note particolari</p>
                <p className="text-xs text-[var(--fc-foreground-muted)]">{analysisResult.contractFields.notes}</p>
              </div>
            )}

            {applyMutation.isError && (
              <p className="text-sm text-[var(--fc-danger)]">Errore durante l'applicazione. Riprova.</p>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <Button variant="secondary" onClick={() => { setAnalysisResult(null); setAnalyzingId(null) }}>
                Annulla
              </Button>
              <Button
                onClick={() => {
                  if (analyzingId) {
                    applyMutation.mutate({ contractId: analyzingId, result: analysisResult })
                  }
                }}
                disabled={applyMutation.isPending || !analyzingId}
              >
                {applyMutation.isPending ? 'Applicazione...' : 'Applica e crea schedule'}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal upload documento */}
      <Modal
        open={Boolean(uploadContractId)}
        onClose={() => { setUploadContractId(null); setPendingFiles([]) }}
        title="Allega documento al contratto"
        size="md"
      >
        <div className="space-y-4">
          <FileUpload
            files={pendingFiles}
            onAdd={(files) => setPendingFiles((prev) => [...prev, ...files])}
            onRemove={(index) => setPendingFiles((prev) => prev.filter((_, i) => i !== index))}
            label="Seleziona o trascina i documenti"
            accept={{
              'image/*': ['.jpg', '.jpeg', '.png', '.webp'],
              'application/pdf': ['.pdf'],
            }}
          />
          {uploadMutation.isError && (
            <p className="text-sm text-[var(--fc-danger)]">Errore durante il caricamento. Riprova.</p>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => { setUploadContractId(null); setPendingFiles([]) }}>
              Annulla
            </Button>
            <Button
              onClick={handleUploadConfirm}
              disabled={pendingFiles.length === 0 || uploadMutation.isPending}
            >
              {uploadMutation.isPending ? 'Caricamento...' : 'Allega'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
